import { Slot } from './Slot'
import type { VariantProps } from '@joyful/utils'
import { tv } from '@joyful/utils'
import type { ComponentProps, ReactElement } from 'react'
export const iconBubbleVariants = tv({
  base: 'flex shrink-0 items-center justify-center overflow-hidden rounded-full [&_svg]:block [&_svg]:stroke-2',

  defaultVariants: {
    contrast: 'default',
    size: 'sm',
    variant: 'neutral',
  },

  slots: {
    icon: 'block size-4 stroke-2',
  },

  variants: {
    contrast: {
      default: {
        base: 'text-tremor-content-inverted',
        icon: 'text-tremor-content-inverted',
      },
      inverse: {
        base: 'text-tremor-content-emphasis',
        icon: 'text-tremor-content-emphasis',
      },
    },

    size: {
      lg: {
        base: 'size-10 [&_svg]:size-5',
        icon: 'size-5',
      },
      md: {
        base: 'size-8 [&_svg]:size-4',
        icon: 'size-4',
      },
      sm: {
        base: 'size-6 [&_svg]:size-3.5',
        icon: 'size-3.5',
      },
      xs: {
        base: 'size-5 [&_svg]:size-2.5',
        icon: 'size-2.5',
      },
    },

    variant: {
      agent: {
        // Outlined-only variant for AI-agent attribution. The bubble border
        // and the icon use the same brand color so the icon visually "owns"
        // the chip without competing with the timeline body copy.
        base: 'bg-tremor-background border border-tremor-brand text-tremor-brand',
        icon: 'text-tremor-brand',
      },
      brand: {
        base: 'bg-tremor-brand',
      },
      error: {
        base: 'bg-red-600',
      },
      info: {
        base: 'bg-blue-500',
      },
      neutral: {
        base: 'bg-tremor-content-subtle',
      },
      success: {
        base: 'bg-emerald-500',
      },
      warning: {
        base: 'bg-yellow-600',
      },
    },
  },
})

export interface IconBubbleProps
  extends
    Omit<ComponentProps<'div'>, 'className'>,
    VariantProps<typeof iconBubbleVariants> {
  children: ReactElement
}

export function IconBubble({
  children,
  contrast,
  size,
  variant,
  ...props
}: IconBubbleProps) {
  const classNames = iconBubbleVariants({
    contrast,
    size,
    variant,
  })
  return (
    <div {...props} className={classNames.base()}>
      <Slot className={classNames.icon()}>{children}</Slot>
    </div>
  )
}
